<template>
	<div id="wrapper">
		<SidebarView :reports="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="container-fluid">
					<div class="card card-shape home-box">
						<div class="card-header">
							<div class="row align-items-center">
								<div class="col-6">
									<h6 class="m-0 fw-bold text-green">Cash Flow</h6>
								</div>
								<div class="col-6 text-end">
									<b-button
										@click="exportExcel"
										:disabled="isExporting || isBusy"
										class="btn-sm me-2 akkurate-green-btn akkurate-small"
										title="Export Excel"
									>
										<font-awesome-icon :icon="['fas', 'file-excel']" />
									</b-button>
									<report-filter :form="form" @filterAction="filterAction" :filter="filter"
										:ytd-terms-uses-financial-year-dates="false"></report-filter>
								</div>
							</div>
							<div class="row">
								<div class="col-12 small mt-2">
									Branch(es) : {{ branch_list_display }}
								</div>
							</div>
						</div>

						<div class="card-body">
							<statement-institution :institution="institution" :current_date="false"
								:title="'CASH FLOW'"></statement-institution>

							<div class="row mx-1">
								<div class="col-12 text-end">
									<span class="mt-2">From: <strong>{{ form.start_date }}</strong> To: <strong>{{ form.end_date }}</strong></span>
								</div>
							</div>

							<div v-if="isBusy" class="text-center text-danger my-2">
								<b-spinner class="align-middle"></b-spinner>
								<strong>Loading...</strong>
							</div>

							<div v-else class="row mx-1">
								<div class="col-12 col-md-12 mb-3">
									<div class="row g-3">
										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="akkurate-dark small">Opening Balance</div>
												<div class="fw-bold fs-5">
													{{ institution?.currency_code }}
													{{ displayNumber(Number(opening_balance).toFixed(2)) }}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="col-12 col-md-12 mb-3">
									<div class="row g-3">
										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="fw-bold akkurate-green mb-2">Operating</div>
												<div class="d-flex justify-content-between">
													<span>Inflow</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(operating.inflow).toFixed(2)) }}</span>
												</div>
												<div class="d-flex justify-content-between">
													<span>Outflow</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(operating.outflow).toFixed(2)) }}</span>
												</div>
												<div class="d-flex justify-content-between">
													<span>Net</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(operating.net).toFixed(2)) }}</span>
												</div>
											</div>
										</div>

										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="fw-bold akkurate-green mb-2">Investing</div>
												<div class="d-flex justify-content-between">
													<span>Inflow</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(investing.inflow).toFixed(2)) }}</span>
												</div>
												<div class="d-flex justify-content-between">
													<span>Outflow</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(investing.outflow).toFixed(2)) }}</span>
												</div>
												<div class="d-flex justify-content-between">
													<span>Net</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(investing.net).toFixed(2)) }}</span>
												</div>
											</div>
										</div>

										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="fw-bold akkurate-green mb-2">Financing</div>
												<div class="d-flex justify-content-between">
													<span>Inflow</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(financing.inflow).toFixed(2)) }}</span>
												</div>
												<div class="d-flex justify-content-between">
													<span>Outflow</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(financing.outflow).toFixed(2)) }}</span>
												</div>
												<div class="d-flex justify-content-between">
													<span>Net</span>
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(financing.net).toFixed(2)) }}</span>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="col-12 col-md-12 mb-3">
									<div class="row g-3">
										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="akkurate-dark small">Net Change</div>
												<div class="fw-bold fs-5">
													{{ institution?.currency_code }}
													{{ displayNumber(Number(net_change).toFixed(2)) }}
												</div>
											</div>
										</div>

										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="akkurate-dark small">Closing Balance</div>
												<div class="fw-bold fs-5">
													{{ institution?.currency_code }}
													{{ displayNumber(Number(closing_balance).toFixed(2)) }}
												</div>
											</div>
										</div>

										<div class="col-12 col-md-4">
											<div class="home-box p-3">
												<div class="akkurate-dark small">Actual Cash GL Balance</div>
												<div class="fw-bold fs-5">
													{{ institution?.currency_code }}
													{{ displayNumber(Number(actual_cash_gl_balance).toFixed(2)) }}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="col-12 col-md-12">
									<div class="home-box p-3">
										<div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
											<div>
												<div class="fw-bold mb-1">Reconciliation</div>
												<div class="small akkurate-dark">
													Actual Cash GL Balance:
													<span class="fw-bold">{{ institution?.currency_code }} {{ displayNumber(Number(actual_cash_gl_balance).toFixed(2)) }}</span>
												</div>
											</div>
											<div>
												<span v-if="is_balanced" class="badge akkurate-blue">Reconciled</span>
												<span v-else class="badge akkurate-danger-bg">Not reconciled</span>
											</div>
										</div>

										<div class="mt-3">
											<b-button
												size="sm"
												class="btn-sm me-2 akkurate-green-btn akkurate-small border-0"
												@click="diagnostics_open = !diagnostics_open"
											>
												{{ diagnostics_open ? "Hide diagnostics" : "View diagnostics" }}
											</b-button>
											<b-collapse :visible="diagnostics_open">
												<div class="row mt-3">
													<div class="col-12 col-md-6">
														<div class="small akkurate-dark fw-bold">Unclassified rows</div>
														<div class="fw-bold">{{ displayNumber(Number(diagnostics.unclassified_rows).toFixed(0)) }}</div>
													</div>
													<div class="col-12 col-md-6">
														<div class="small akkurate-dark fw-bold">Missing counter rows</div>
														<div class="fw-bold">{{ displayNumber(Number(diagnostics.missing_counter_rows).toFixed(0)) }}</div>
													</div>
													<div class="col-12 col-md-6">
														<div class="small akkurate-dark fw-bold">Duplicate counter rows</div>
														<div class="fw-bold">{{ displayNumber(Number(diagnostics.duplicate_counter_rows).toFixed(0)) }}</div>
													</div>
													<div class="col-12 col-md-6">
														<div class="small akkurate-dark fw-bold">Unpaired cash legs rows</div>
														<div class="fw-bold">{{ displayNumber(Number(diagnostics.unpaired_cash_legs_rows).toFixed(0)) }}</div>
													</div>
													<div class="col-12">
														<div class="small akkurate-dark fw-bold">Net change (all cash)</div>
														<div class="fw-bold">
															{{ institution?.currency_code }}
															{{ displayNumber(Number(diagnostics.net_change_all_cash).toFixed(2)) }}
														</div>
													</div>
												</div>
											</b-collapse>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
			<!-- AI -->
			<ChatBot />
			<!-- End AI -->
			<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>
		</div>
	</div>
</template>

<script>
	import SidebarView from "@/views/main/components/Sidebar.vue";
	import TopbarView from "@/views/main/components/Topbar.vue";
	import Footer from "@/views/main/components/Footer.vue";
	import StatementInstitution from "@/views/main/components/institution/StatementInstitution.vue";
	import ApiService from "@/core/services/api.service";
	import ReportFilter from "../form/ReportFilter.vue";
	import { showFinancialReportApiError } from "@/utils/financialReportApiErrors";
	import ChatBot from "@/views/components/AI/ChatBot.vue";

	export default {
		components: {
			SidebarView,
			TopbarView,
			Footer,
			StatementInstitution,
			ReportFilter,
			ChatBot,
		},
		data() {
			return {
				isBusy: false,
				isExporting: false,
				institution: {
					name: "",
					address: "",
					postal: "",
					currency: "",
					motto: "",
					currency_code: "",
					logo: "",
				},
				diagnostics_open: false,
				opening_balance: 0,
				net_change: 0,
				closing_balance: 0,
				operating: { inflow: 0, outflow: 0, net: 0 },
				investing: { inflow: 0, outflow: 0, net: 0 },
				financing: { inflow: 0, outflow: 0, net: 0 },
				actual_cash_gl_balance: 0,
				is_balanced: false,
				diagnostics: {
					unclassified_rows: 0,
					missing_counter_rows: 0,
					duplicate_counter_rows: 0,
					unpaired_cash_legs_rows: 0,
					net_change_all_cash: 0,
				},
				form: {
					start_date: new Date().toISOString().split("T")[0],
					end_date: new Date().toISOString().split("T")[0],
					branches: [],
					branch_options: [],
					products: [],
					product_options: [],
					users: [],
					users_options: [],
					status: [],
					coa: [],
					coa_options: [],
					terms: "YTD",
					terms_options: ["YTD", "Periodic"],
					categories: [],
					loan_status: [],
				},
				filter: {
					branches: true,
					products: false,
					users: false,
					status: false,
					condition: false,
					terms: false,
					coa: false,
					voucher_status: false,
					comission_type: false,
					purpose: false,
					start_date: true,
					end_date: true,
					loan_status: false,
				},
				branch_list_display: "",
			};
		},
		created() {
			this.getInstitution();
			this.initBranchDropdown();
			this.getCashFlow();
		},
		methods: {
			async getInstitution() {
				await ApiService.get("/settings/institution")
					.then((response) => {
						this.institution = response.data.data;
					})
					.catch(() => {})
					.finally(() => {});
			},
			async initBranchDropdown() {
				await ApiService.query("/reports/controls/products/dropdown")
					.then((response) => {
						this.form.branch_options = response.data.branches || [];
						// keep a friendly default label (all branches)
						this.branch_list_display =
							this.form.branch_options.map((b) => b?.name).join(", ") || "";
					})
					.catch(() => {});
			},
			filterAction() {
				this.getCashFlow();
			},
			buildParams() {
				const selected = this.form.branches || [];
				const all = this.form.branch_options || [];

				const start_date = this.form.start_date;
				const end_date = this.form.end_date;

				const params = {
					start_date,
					end_date,
				};

				const selectedAll =
					selected.length > 0 && all.length > 0 && selected.length === all.length;

				// Backend contract expects optional singular `branch_id`.
				// If user selected multiple branches (but not "all branches"), block and ask to choose one.
				if (!selectedAll && selected.length > 1) {
					swal.fire({
						icon: "error",
						title: "Invalid branch selection",
						text: "Please select only one branch, or select all branches.",
						showCancelButton: false,
						confirmButtonText: "OK",
						buttonsStyling: false,
					});
					return null;
				}

				// When "All branches" is selected (or none selected), do not send branch_id.
				if (!selectedAll && selected.length > 0) {
					params.branch_id = selected[0].id;
				}

				return params;
			},
			async getCashFlow() {
				// Client-side required + ordering checks.
				if (!this.form.start_date || !this.form.end_date) {
					swal.fire({
						icon: "error",
						title: "Invalid dates",
						text: "Start Date and End Date are required.",
						showCancelButton: false,
						confirmButtonText: "OK",
						buttonsStyling: false,
					});
					return;
				}

				if (this.form.end_date < this.form.start_date) {
					swal.fire({
						icon: "error",
						title: "Invalid date range",
						text: "End Date must be greater than or equal to Start Date.",
						showCancelButton: false,
						confirmButtonText: "OK",
						buttonsStyling: false,
					});
					return;
				}

				this.isBusy = true;
				try {
					const params = this.buildParams();
					if (!params) {
						return;
					}

					// IMPORTANT: ApiService.get() in this repo does not accept axios config,
					// so query params must be sent via ApiService.query(resource, { params }).
					const response = await ApiService.query(
						"/reports/financials/cash-flow",
						{ params }
					);

					const payload = response?.data?.data || {};
					const d = payload.diagnostics || {};

					this.opening_balance = Number(payload.opening_balance ?? 0);
					this.operating = payload.operating || { inflow: 0, outflow: 0, net: 0 };
					this.investing = payload.investing || { inflow: 0, outflow: 0, net: 0 };
					this.financing = payload.financing || { inflow: 0, outflow: 0, net: 0 };
					this.net_change = Number(payload.net_change ?? 0);
					this.closing_balance = Number(payload.closing_balance ?? 0);
					this.actual_cash_gl_balance = Number(payload.actual_cash_gl_balance ?? 0);
					this.is_balanced = Boolean(payload.is_balanced);
					// Open diagnostics by default when not reconciled; user can still toggle.
					this.diagnostics_open = !this.is_balanced;
					this.diagnostics = {
						unclassified_rows: Number(d.unclassified_rows ?? 0),
						missing_counter_rows: Number(d.missing_counter_rows ?? 0),
						duplicate_counter_rows: Number(d.duplicate_counter_rows ?? 0),
						unpaired_cash_legs_rows: Number(d.unpaired_cash_legs_rows ?? 0),
						net_change_all_cash: Number(d.net_change_all_cash ?? 0),
					};

					// Update branch label for the currently-selected request.
					const selected = this.form.branches || [];
					const all = this.form.branch_options || [];
					const selectedAll =
						selected.length > 0 && all.length > 0 && selected.length === all.length;
					if (selectedAll || selected.length === 0) {
						this.branch_list_display = all.map((b) => b?.name).join(", ");
					} else {
						this.branch_list_display = selected.map((b) => b?.name).join(", ");
					}
				} catch (error) {
					showFinancialReportApiError(this, error);
					// Render empty-but-safe defaults
					this.opening_balance = 0;
					this.operating = { inflow: 0, outflow: 0, net: 0 };
					this.investing = { inflow: 0, outflow: 0, net: 0 };
					this.financing = { inflow: 0, outflow: 0, net: 0 };
					this.net_change = 0;
					this.closing_balance = 0;
					this.actual_cash_gl_balance = 0;
					this.is_balanced = false;
					this.diagnostics_open = true;
					this.diagnostics = {
						unclassified_rows: 0,
						missing_counter_rows: 0,
						duplicate_counter_rows: 0,
						unpaired_cash_legs_rows: 0,
						net_change_all_cash: 0,
					};
				} finally {
					this.isBusy = false;
				}
			},
			async exportExcel() {
				// Reuse the same validations / params logic as "Run".
				if (!this.form.start_date || !this.form.end_date) {
					swal.fire({
						icon: "error",
						title: "Invalid dates",
						text: "Start Date and End Date are required.",
						showCancelButton: false,
						confirmButtonText: "OK",
						buttonsStyling: false,
					});
					return;
				}
				if (this.form.end_date < this.form.start_date) {
					swal.fire({
						icon: "error",
						title: "Invalid date range",
						text: "End Date must be greater than or equal to Start Date.",
						showCancelButton: false,
						confirmButtonText: "OK",
						buttonsStyling: false,
					});
					return;
				}

				const params = this.buildParams();
				if (!params) return;

				this.isExporting = true;
				try {
					const res = await ApiService.query(
						"/reports/financials/cash-flow/export-excel",
						{ params }
					);
					const body = res?.data || {};
					const url = body?.download_url;
					if (!url) {
						throw new Error("No download_url returned");
					}

					if (window.toast?.fire) {
						window.toast.fire({
							icon: "success",
							title: body?.message || "Export ready",
						});
					}
					window.open(url, "_blank");
				} catch (error) {
					if (window.toast?.fire) {
						window.toast.fire({
							icon: "error",
							title: "Export failed",
						});
					}
					showFinancialReportApiError(this, error);
				} finally {
					this.isExporting = false;
				}
			},
		},
	};
</script>

